import { z } from "zod";
import { QuestionAnswersMapSchema } from "./canvas";
import { SlideElementSchema } from "./draggables";
import {
  GrammarAssessmentResultSchema,
  PronunciationScoresSchema,
} from "./pronunciation-coaching.schema";
import {
  GrammarResponseSlideDataSchema,
  GrammarSlideDataSchema,
  HandWritingSlideDataSchema,
  OtherSlideDataSchema,
  PhonicsDecodeSlideDataSchema,
  PhonicsEncodeSlideDataSchema,
  ReadingSlideDataSchema,
  SlideDomainEnum,
  SlideLimitationSchema,
  WordSlideDataSchema,
  WordVocabularySlideDataSchema,
} from "./slide";
import { SlideTemplateEnum } from "./slideTemplate";

const PromptSnapshotBaseSchema = z.object({
  name: z.string(),
  limitation: SlideLimitationSchema.default({}),
});

/** What the student was asked, copied at answer time so the record survives later admin edits. */
export const PromptSnapshotSchema = z.discriminatedUnion("template", [
  PromptSnapshotBaseSchema.extend({
    template: z.literal("vocabulary"),
    slideData: WordSlideDataSchema,
  }),
  PromptSnapshotBaseSchema.extend({
    template: z.literal("wordCanvas"),
    slideData: WordSlideDataSchema,
    elements: z.array(SlideElementSchema).default([]),
  }),
  PromptSnapshotBaseSchema.extend({
    template: z.literal("listenRepeat"),
    slideData: WordVocabularySlideDataSchema,
  }),
  PromptSnapshotBaseSchema.extend({
    template: z.literal("conversation"),
    slideData: GrammarSlideDataSchema,
  }),
  PromptSnapshotBaseSchema.extend({
    template: z.literal("audioRevealPrompt"),
    slideData: GrammarSlideDataSchema,
  }),
  PromptSnapshotBaseSchema.extend({
    template: z.literal("videoQuestion"),
    slideData: GrammarResponseSlideDataSchema,
  }),
  PromptSnapshotBaseSchema.extend({
    template: z.literal("passageReading"),
    slideData: ReadingSlideDataSchema,
  }),
  PromptSnapshotBaseSchema.extend({
    template: z.literal("freeformCanvas"),
    slideData: OtherSlideDataSchema,
    elements: z.array(SlideElementSchema).default([]),
  }),
  PromptSnapshotBaseSchema.extend({
    template: z.literal("soundToSpelling"),
    slideData: PhonicsEncodeSlideDataSchema,
  }),
  PromptSnapshotBaseSchema.extend({
    template: z.literal("spellingToSound"),
    slideData: PhonicsDecodeSlideDataSchema,
  }),
  PromptSnapshotBaseSchema.extend({
    template: z.literal("handWriting"),
    slideData: HandWritingSlideDataSchema,
  }),
]);

/** One captured utterance. The coaching server persists it and the client never supplies these
 * numbers; a play may produce several takes, and the last one is the score. `scores` is null when
 * nothing assessed the audio — a `recordOnly` slide keeps the recording without paying for Azure. */
export const SpeechTakeSchema = z.object({
  recognizedText: z.string(),
  scores: PronunciationScoresSchema.nullable().default(null),
  recordingId: z.string().nullable().default(null),
});

const LabeledScoreSchema = z.object({
  label: z.string(),
  score: z.number(),
});

const QuizAnswerSchema = z.object({
  id: z.string(),
  answer: z.string(),
  isCorrect: z.boolean(),
  score: z.number(),
});

/** The student-facing exchange only; the coaching server's transcript keeps the diagnostics. */
const ConversationTurnSchema = z.object({
  recognizedText: z.string().default(""),
  assistantText: z.string().default(""),
  pronScore: z.number().nullable().default(null),
  /** The student's own audio for this turn; the teacher's is synthesized on replay. */
  recordingId: z.string().nullable().default(null),
});

/**
 * One prompt word the student wrote. The client sends `text` alone; the server fills the rest from
 * what the grader held during the play, exactly as it does for speech takes.
 */
const HandWritingAttemptSchema = z.object({
  text: z.string(),
  /** S3 key of the student's writing, like `recordingId`. Null when no image reached the grader. */
  imageId: z.string().nullable().default(null),
  score: z.number().nullable().default(null),
  explanation: z.string().default(""),
});

const CanvasPerQuestionSchema = z.object({
  elementId: z.string(),
  isCorrect: z.boolean(),
});

/** The student's side only; everything that was asked lives in `PromptSnapshotSchema`. */
export const SlideResponseSchema = z.discriminatedUnion("template", [
  z.object({
    template: z.literal("soundToSpelling"),
    answer: z.string(),
    isCorrect: z.boolean(),
  }),

  z.object({
    template: z.literal("spellingToSound"),
    take: SpeechTakeSchema,
    hint: z.string().nullable().default(null),
    explanation: z.string().default(""),
  }),

  z.object({
    template: z.literal("vocabulary"),
    take: SpeechTakeSchema.nullable(),
    syllableScores: z.array(LabeledScoreSchema).default([]),
    wordScores: z.array(LabeledScoreSchema).nullable().default(null),
    explanation: z.string().default(""),
  }),

  z.object({
    template: z.literal("listenRepeat"),
    take: SpeechTakeSchema.nullable(),
    expectedAnswer: z.string().default(""),
    // Word-by-word only in strictMode; the other grading modes score through the LLM instead.
    strictWords: z
      .array(z.object({ word: z.string(), spoken: z.boolean() }))
      .nullable()
      .default(null),
    gradedScore: z.number().nullable().default(null),
    explanation: z.string().default(""),
  }),

  z.object({
    template: z.literal("audioRevealPrompt"),
    take: SpeechTakeSchema,
    expectedAnswer: z.string().default(""),
  }),

  z.object({
    template: z.literal("videoQuestion"),
    checkpoints: z
      .array(
        z.object({
          timestamp: z.number(),
          recognizedText: z.string(),
          expectedAnswer: z.string(),
          score: z.number(),
          explanation: z.string().default(""),
          // The utterance the coaching server held under this checkpoint's timestamp as its part id.
          take: SpeechTakeSchema.nullable().default(null),
        })
      )
      .default([]),
  }),

  z.object({
    template: z.literal("passageReading"),
    paragraphs: z
      .array(
        z.object({
          paragraphId: z.string(),
          score: z.number(),
          take: SpeechTakeSchema.nullable().default(null),
          wordCount: z.number().default(0),
          /** Per-word accuracy in reading order; empty when nothing word-scored the play. */
          words: z.array(z.object({ word: z.string(), accuracyScore: z.number() })).default([]),
          hintedWords: z.array(z.string()).default([]),
        })
      )
      .default([]),
    quizzes: z.array(QuizAnswerSchema).default([]),
  }),

  z.object({
    template: z.literal("freeformCanvas"),
    answers: QuestionAnswersMapSchema.default({}),
    perQuestion: z.array(CanvasPerQuestionSchema).default([]),
    quizzes: z.array(QuizAnswerSchema).default([]),
  }),

  z.object({
    template: z.literal("wordCanvas"),
    answers: QuestionAnswersMapSchema.default({}),
    perQuestion: z.array(CanvasPerQuestionSchema).default([]),
    quizzes: z.array(QuizAnswerSchema).default([]),
  }),

  z.object({
    template: z.literal("handWriting"),
    attempts: z.array(HandWritingAttemptSchema).default([]),
  }),

  // Written only once the conversation closes naturally; a student who leaves early produces no
  // document at all. `breakdown` is null when the closed conversation had nothing gradeable.
  z.object({
    template: z.literal("conversation"),
    breakdown: GrammarAssessmentResultSchema.nullable().default(null),
    turns: z.array(ConversationTurnSchema).default([]),
  }),
]);

/** One completed slide play. Append-only: written once when the slide finishes, never updated. */
export const SlideScoreSchema = z.object({
  id: z.string(),
  studentId: z.string(),
  slideId: z.string(),
  lessonId: z.string(),
  template: SlideTemplateEnum,
  domains: z.array(SlideDomainEnum).default([]),

  // Null = completed but unscored (a recordOnly slide, a canvas with no questions), which is
  // distinct from 0, a real failing score.
  score: z.number().min(0).max(100).nullable(),

  promptSnapshot: PromptSnapshotSchema,
  response: SlideResponseSchema,

  responseTimeMs: z.number().nonnegative(),
  startedAt: z.date(),
  createdAt: z.date(),

  attemptKey: z.string(),

  runId: z.string(),
});

export type PromptSnapshot = z.infer<typeof PromptSnapshotSchema>;
export type SlideResponse = z.infer<typeof SlideResponseSchema>;
export type SpeechTake = z.infer<typeof SpeechTakeSchema>;
export type SlideScore = z.infer<typeof SlideScoreSchema>;
